import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignalREvent } from './useSignalR.tsx';
import { useApiQuery } from './useApi.ts';
import { useTenantContext } from './contexts.tsx';
import { QueryKeys } from './queryKeys.ts';
import { applyLiveEvent } from './reconciliation.ts';

/**
 * Upper bound on the number of alarms a long-lived realtime hook retains in
 * memory. A control-room session left open for a shift can otherwise accumulate
 * every alarm ever raised. We keep the most-recent N (newest first) and evict
 * the oldest beyond the cap, so memory stays bounded regardless of session age.
 */
const MAX_RETAINED_ALARMS = 500;

/**
 * Equipment operational state
 */
export enum EquipmentMode {
  Idle = 'Idle',
  Running = 'Running',
  Paused = 'Paused',
  Error = 'Error',
  Maintenance = 'Maintenance',
}

/**
 * Current equipment state
 */
export interface EquipmentState {
  equipmentId: string;
  mode: EquipmentMode;
  productionLineId?: string;
  currentJobId?: string;
  cycleTime?: number;
  efficiency?: number;
  temperature?: number;
  pressure?: number;
  vibration?: number;
  lastUpdated: string;
  metrics?: Record<string, number>;
}

/**
 * Production job update
 */
export interface ProductionUpdate {
  lineId: string;
  jobId?: string;
  materialId?: string;
  quantity?: number;
  completedQuantity?: number;
  status?: 'Pending' | 'Running' | 'Completed' | 'Failed';
  scheduledEndTime?: string;
  actualEndTime?: string;
  efficiency?: number;
  defectRate?: number;
  timestamp: string;
}

/**
 * Alarm/alert on production line or equipment
 */
export interface Alarm {
  alarmId: string;
  type: 'Warning' | 'Error' | 'Critical';
  code: string;
  message: string;
  entityType: 'Equipment' | 'Line' | 'Job';
  entityId: string;
  createdAt: string;
  resolvedAt?: string;
  details?: Record<string, unknown>;
}

/**
 * Hook to subscribe to equipment state changes
 * Returns real-time equipment state including mode, metrics, and performance
 *
 * @param equipmentId - ID of the equipment to monitor
 * @returns Current equipment state
 *
 * @example
 * const equipmentState = useEquipmentState('lathe-001');
 * if (equipmentState?.mode === EquipmentMode.Running) {
 *   return <RunningIndicator efficiency={equipmentState.efficiency} />;
 * }
 */
export const useEquipmentState = (
  equipmentId: string
): EquipmentState | null => {
  const [state, setState] = useState<EquipmentState | null>(null);
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();

  // Initial-snapshot hydration: fetch the current equipment state once on mount
  // so the hook is populated immediately instead of staying null until the next
  // live event arrives. Uses the shared TanStack-Query data-fetching convention.
  const { data: snapshot } = useApiQuery<Partial<EquipmentState> | null>(
    equipmentId ? `/equipment/${equipmentId}` : '',
    { enabled: Boolean(equipmentId) }
  );

  useEffect(() => {
    if (!snapshot) return;
    // Only seed from the snapshot while no live event has been applied yet, so
    // a slow REST response can never clobber a fresher pushed update.
    setState((prev) => {
      if (prev) return prev;
      return {
        equipmentId,
        mode: (snapshot.mode as EquipmentMode) ?? EquipmentMode.Idle,
        lastUpdated: snapshot.lastUpdated ?? new Date().toISOString(),
        ...snapshot,
      } as EquipmentState;
    });
  }, [snapshot, equipmentId]);

  useSignalREvent<EquipmentState>(
    'EquipmentHub',
    'EquipmentStateChanged',
    (data) => {
      if (data.equipmentId === equipmentId) {
        setState(data);

        // Reconcile: merge the pushed state into the cache with a freshness
        // marker so a slow snapshot/refetch can't roll it back (A5-025), then
        // tenant-scope so the live event cannot evict another tenant (A5-023).
        // Keyed via the named QueryKeys factory so the read and this write can
        // never drift if the endpoint path is refactored (A5-551).
        applyLiveEvent(
          queryClient,
          QueryKeys.equipment(tenantId, equipmentId),
          data,
        );
      }
    }
  );

  return state;
};

/**
 * Hook to subscribe to production line updates
 * Returns latest job status, material progress, and efficiency metrics
 *
 * @param lineId - ID of the production line to monitor
 * @returns Latest production update
 *
 * @example
 * const update = useProductionUpdates('line-001');
 * return (
 *   <ProductionStatus
 *     jobId={update?.jobId}
 *     completedQuantity={update?.completedQuantity}
 *   />
 * );
 */
export const useProductionUpdates = (lineId: string): ProductionUpdate | null => {
  const [update, setUpdate] = useState<ProductionUpdate | null>(null);
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();

  useSignalREvent<ProductionUpdate>(
    'ProductionHub',
    'ProductionUpdated',
    (data) => {
      if (data.lineId === lineId) {
        setUpdate(data);

        // Invalidate relevant queries via the named factory, tenant-scoped, so
        // the keys stay in lock-step with the reads (A5-551).
        void queryClient.invalidateQueries({
          queryKey: QueryKeys.production(tenantId, lineId),
        });
        if (data.jobId) {
          void queryClient.invalidateQueries({
            queryKey: QueryKeys.job(tenantId, data.jobId),
          });
        }
      }
    }
  );

  return update;
};

/**
 * Hook to subscribe to alarms and alerts
 * Returns list of active alarms, optionally filtered by topic/entity
 *
 * @param topic - Optional topic filter (e.g., 'equipment', 'line', 'system')
 * @returns List of active alarms
 *
 * @example
 * const alarms = useAlarms('equipment');
 * return (
 *   <AlertPanel alarms={alarms} onAcknowledge={handleAcknowledge} />
 * );
 */
export const useAlarms = (topic?: string): Alarm[] => {
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();

  // Initial-snapshot hydration: fetch the currently-active alarms once on mount
  // so the panel isn't empty until the next alarm is raised. Mapped defensively
  // from the active-alarms projection into the hook's Alarm shape.
  const { data: activeSnapshot } = useApiQuery<unknown[]>('/alarms/active');

  useEffect(() => {
    if (!Array.isArray(activeSnapshot) || activeSnapshot.length === 0) return;
    const mapped: Alarm[] = activeSnapshot.map((raw) => {
      const r = raw as Record<string, unknown>;
      return {
        alarmId: String(r.alarmId ?? r.id ?? ''),
        type: (r.type as Alarm['type']) ?? 'Warning',
        code: String(r.code ?? r.priority ?? ''),
        message: String(r.message ?? r.name ?? ''),
        entityType: (r.entityType as Alarm['entityType']) ?? 'Equipment',
        entityId: String(r.entityId ?? r.id ?? ''),
        createdAt: String(r.createdAt ?? r.activatedAt ?? new Date().toISOString()),
        resolvedAt: r.resolvedAt ? String(r.resolvedAt) : undefined,
        details: (r.details as Record<string, unknown>) ?? undefined,
      };
    });
    setAlarms((prev) => {
      // Merge the snapshot under any already-pushed live alarms (live wins on
      // id collision), then bound the retained set to the most-recent N.
      const seen = new Set(prev.map((a) => a.alarmId));
      const merged = [
        ...prev,
        ...mapped.filter((a) => a.alarmId && !seen.has(a.alarmId)),
      ];
      return merged.slice(0, MAX_RETAINED_ALARMS);
    });
  }, [activeSnapshot]);

  // Subscribe to new alarms
  useSignalREvent<Alarm>(
    'AlarmHub',
    'AlarmRaised',
    (data) => {
      if (!topic || data.entityType.toLowerCase() === topic) {
        setAlarms((prevAlarms) => {
          // Avoid duplicates
          if (prevAlarms.some((a) => a.alarmId === data.alarmId)) {
            return prevAlarms;
          }
          // Prepend newest and bound the retained set so a long-lived session
          // cannot grow the array without limit (ring-buffer by count).
          return [data, ...prevAlarms].slice(0, MAX_RETAINED_ALARMS);
        });

        // Invalidate alarms cache, tenant-scoped, via the named factory (A5-551).
        void queryClient.invalidateQueries({
          queryKey: QueryKeys.alarms(tenantId),
        });
      }
    }
  );

  // Subscribe to resolved alarms
  useSignalREvent<{ alarmId: string; resolvedAt: string }>(
    'AlarmHub',
    'AlarmResolved',
    (data) => {
      setAlarms((prevAlarms) =>
        prevAlarms.map((alarm) =>
          alarm.alarmId === data.alarmId
            ? { ...alarm, resolvedAt: data.resolvedAt }
            : alarm
        )
      );

      void queryClient.invalidateQueries({
        queryKey: QueryKeys.alarms(tenantId),
      });
    }
  );

  return alarms;
};

/**
 * Hook to subscribe to equipment state changes for a specific production line
 * Returns aggregated state for all equipment on the line
 *
 * @param lineId - ID of the production line
 * @returns Map of equipment ID to current state
 *
 * @example
 * const equipmentStates = useLineEquipmentStates('line-001');
 * Object.entries(equipmentStates).forEach(([equipmentId, state]) => {
 *   console.log(`${equipmentId}: ${state.mode}`);
 * });
 */
export const useLineEquipmentStates = (
  lineId: string
): Record<string, EquipmentState> => {
  const [states, setStates] = useState<Record<string, EquipmentState>>({});
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();

  useSignalREvent<{
    lineId: string;
    equipment: EquipmentState[];
  }>(
    'EquipmentHub',
    'LineEquipmentStatusUpdated',
    (data) => {
      if (data.lineId === lineId) {
        const stateMap: Record<string, EquipmentState> = {};
        data.equipment.forEach((eq) => {
          stateMap[eq.equipmentId] = eq;
        });
        setStates(stateMap);

        // Invalidate line equipment cache, tenant-scoped, via the named
        // factory (A5-551).
        void queryClient.invalidateQueries({
          queryKey: QueryKeys.lineEquipment(tenantId, lineId),
        });
      }
    }
  );

  return states;
};

/**
 * Hook to get KPI metrics for a production line
 * Aggregates efficiency, throughput, and quality metrics
 *
 * @param lineId - ID of the production line
 * @returns KPI metrics object
 *
 * @example
 * const kpis = useLineKPIs('line-001');
 * return <KPIDisplay overallEquipmentEffectiveness={kpis.oee} />;
 */
export interface LineKPIs {
  oee?: number; // Overall Equipment Effectiveness
  efficiency?: number;
  availability?: number;
  quality?: number;
  throughput?: number;
  cycleTimeVariance?: number;
  defectRate?: number;
  lastUpdated: string;
}

export const useLineKPIs = (lineId: string): LineKPIs | null => {
  const [kpis, setKpis] = useState<LineKPIs | null>(null);
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();

  // Line KPIs ride the ProductionHub (there is no separate analytics hub):
  // line OEE / efficiency / throughput are production-line telemetry, broadcast
  // on the same hub that carries job-order and material-flow updates.
  useSignalREvent<LineKPIs>(
    'ProductionHub',
    'LineKPIsUpdated',
    (data) => {
      if ('lastUpdated' in data) {
        setKpis(data);

        void queryClient.invalidateQueries({
          queryKey: QueryKeys.lineKpis(tenantId, lineId),
        });
      }
    }
  );

  return kpis;
};

/**
 * Hook to subscribe to maintenance predictions
 * Returns predictive maintenance alerts and recommended actions
 *
 * @param equipmentId - ID of the equipment
 * @returns Maintenance prediction data
 *
 * @example
 * const prediction = useMaintenancePrediction('lathe-001');
 * if (prediction?.riskLevel === 'High') {
 *   return <MaintenanceAlert prediction={prediction} />;
 * }
 */
export interface MaintenancePrediction {
  equipmentId: string;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  confidenceScore: number;
  estimatedTimeToFailure?: number; // hours
  recommendedActions: string[];
  lastUpdated: string;
}

export const useMaintenancePrediction = (
  equipmentId: string
): MaintenancePrediction | null => {
  const [prediction, setPrediction] = useState<MaintenancePrediction | null>(
    null
  );
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();

  // Maintenance predictions are keyed by equipment id and ride the EquipmentHub
  // (there is no separate predictive-maintenance hub): the prediction is
  // equipment telemetry, delivered on the same hub as equipment state changes.
  useSignalREvent<MaintenancePrediction>(
    'EquipmentHub',
    'MaintenancePredictionUpdated',
    (data) => {
      if (data.equipmentId === equipmentId) {
        setPrediction(data);

        void queryClient.invalidateQueries({
          queryKey: QueryKeys.maintenancePrediction(tenantId, equipmentId),
        });
      }
    }
  );

  return prediction;
};
